import { memo as Memo, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';

//* API
import api from '@/utils/api';

//* Helpers
import breakString from '@helpers/breakString';

//* Components
import Form from '@components/global/Form';
import Text from '@components/global/Text';
import MinTitle from '@components/global/MinTitle';
import CustomLottie from '@/components/global/CustomLottie';

//* JSONs
import animJSON from './animJSON.json';

//* Styles
import TeamFormStyle from './style';

const TeamForm = Memo(({ title, active }) => {
	//! States
	const [isSend, setIsSend] = useState(false);

	//! Refs
	const formRef = useRef();

	//! Form Object
	const obj = {
		realTimeValidate: true,
		submitText: 'Submit',
		teamFormBtn: true,
		sizes: [6, 1, 1, 1],
		rowTitle: ['tellUsBitAbout', 'Position', 'experience', 'whatsMotivation'],
		rowTitleContClassName: `rowTitleContClassName`,
		rowTitleClassName: `rowTitleClassName`,
		fields: {
			your_name: {
				fieldType: 'input',
				props: {
					value: '',
					required: {
						min: 2,
					},
					type: 'text',
					placeholder: 'name',
				},
			},
			your_last_name: {
				fieldType: 'input',
				props: {
					value: '',
					required: {
						min: 2,
					},
					type: 'text',
					placeholder: 'lastName',
				},
			},
			your_email: {
				fieldType: 'input',
				props: {
					value: '',
					required: true,
					type: 'email',
					placeholder: 'email',
				},
			},
			your_number: {
				fieldType: 'input',
				props: {
					value: '',
					required: {
						min: 2,
					},
					type: 'number',
					placeholder: 'phoneNumber',
				},
			},
			your_resume: {
				fieldType: 'input',
				props: {
					value: '',
					required: {
						min: 2,
					},
					type: 'url',
					placeholder: 'resume',
				},
			},
			your_portfolio_link: {
				fieldType: 'input',
				props: {
					value: '',
					required: {
						min: 2,
					},
					type: 'link',
					placeholder: 'portfolioLink',
				},
			},
			position: {
				fieldType: 'checkbox',
				checkboxClassName: 'smallCheckbox',
				props: {
					required: {
						min: 0,
						max: 1,
					},
					style: 'default',
					inputType: 'radio',
					list: [
						{
							label: 'UI/UX designer',
							value: 'UI/UX designer',
							checked: 0,
						},
						{
							label: 'Graphic designer',
							value: 'Graphic designer',
							checked: 0,
						},
						{
							label: 'Motion designer',
							value: 'Motion designer',
							checked: 0,
						},
						{
							label: 'Illustrator',
							value: 'Illustrator',
							checked: 0,
						},
						{
							label: 'Sales manager',
							value: 'Sales manager',
							checked: 0,
						},
						{
							label: 'Account manager',
							value: 'Account manager',
							checked: 0,
						},
						{
							label: 'Project manager',
							value: 'Project manager',
							checked: 0,
						},
						{
							label: 'Front-end developer',
							value: 'Front-end developer',
							checked: 0,
						},
						{
							label: 'Back-end developer',
							value: 'Back-end developer',
							checked: 0,
						},
					],
				},
			},
			experience: {
				fieldType: 'checkbox',
				checkboxClassName: 'smallCheckbox',
				props: {
					required: {
						min: 0,
						max: 1,
					},
					style: 'default',
					inputType: 'radio',
					list: [
						{
							label: 'intern',
							value: 'intern',
							checked: 0,
						},
						{
							label: 'junior',
							value: 'junior',
							checked: 0,
						},
						{
							label: 'middle',
							value: 'middle',
							checked: 0,
						},
						{
							label: 'senior',
							value: 'senior',
							checked: 0,
						},
						{
							label: 'team lead',
							value: 'team lead',
							checked: 0,
						},
					],
				},
			},
			your_message: {
				fieldType: 'textarea',
				textareaClassName: `textareaClassName`,
				props: {
					required: {
						min: 10,
						max: 250,
					},
					type: 'text',
					placeholder: 'message',
				},
			},
		},
	};

	//! Refs
	const topRef = useRef();

	//! Animation
	const tl = useMemo(() => gsap.timeline({ delay: 0.8 }), []);

	useEffect(() => {
		if (topRef.current && topRef.current?.childNodes[0]?.childNodes[0]?.childNodes[0]?.childNodes) {
			const arr = [topRef.current.childNodes[0].childNodes[0].childNodes[0].childNodes, topRef.current.childNodes[0].childNodes[0].childNodes[1].childNodes, topRef.current.childNodes[0].childNodes[0].childNodes[2].childNodes, topRef.current.childNodes[0].childNodes[0].childNodes[3].childNodes];
			if (active) {
				tl.staggerTo(
					[arr],
					0,
					{
						opacity: 1,
					},
					0.2
				);
			} else {
				tl.staggerTo(
					[arr],
					0,
					{
						opacity: 0,
					},
					0
				);
			}
		}
	}, [active]);

	const submit = useCallback((values) => {
		setIsSend('loading');

		api.post('team-member-cv', values).then((res) => {
			if (res.status === 'mail_sent') {
				setIsSend(true);
				formRef.current.resetFields();
			}
		});
	}, []);

	return (
		<TeamFormStyle className={'teamFormCont'}>
			<MinTitle
				active={active}
				title={breakString(title)}
			/>

			<div
				ref={topRef}
				className={`TeamFormWrap ${isSend === 'loading' ? 'loading' : ''}`}>
				<Form
					{...obj}
					ref={formRef}
					submit={submit}
					className={'inputClass'}
					wrapContClass={'inputWrapClass'}
					submitText={isSend === 'loading' ? 'loading' : 'submit'}
				/>
			</div>

			<div className={'successMessageCont'}>
				<div className={'successMessage'}>
					{isSend && isSend !== 'loading' ? (
						<Text
							tag='h3'
							text='teamFormThankYouMsg'
							className={`h6 font-bold msg`}
						/>
					) : null}

					<div className={'animCont'}>
						<CustomLottie
							isStopped={false}
							animData={animJSON}
						/>
					</div>
				</div>
			</div>
		</TeamFormStyle>
	);
});

export default TeamForm;
